// src/components/roles/RoleDetailModal/RoleDetailModal.tsx
import React, { useEffect, useState } from 'react';
import {
  Grid,
  Box,
  Typography,
  Chip,
  Divider,
  CircularProgress,
  Button,
  Alert,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  AdminPanelSettings as RoleIcon,
  People as PeopleIcon,
  CalendarToday as CalendarIcon,
  Block as BlockIcon,
  Info as InfoIcon,
  EditOutlined as EditIcon,
} from '@mui/icons-material';
import { DetailModal } from '../../ui/Modal';
import { Role, RoleUser, Permission } from '../../../types/role.types';
import { isSystemRole } from '../../../types/role.types';
import StatusChip from '../../ui/StatusChip/StatusChip';
import { RolesServiceHelpers } from '../../../utils/api-helpers/RolesService.helpers';
import { RoleDetailModalStyles } from './RoleDetailModal.styles';
import { getModuleById, getMenuById } from '../../../config/system-structure';

export interface RoleDetailModalProps {
  open: boolean;
  role: Role | null;
  onClose: () => void;
  onEditRole?: (id: string) => void;
  className?: string;
}

const RoleDetailModalComponent: React.FC<RoleDetailModalProps> = ({
  open,
  role,
  onEditRole,
  onClose,
  className,
}) => {
  const [users, setUsers] = useState<RoleUser[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingPermissions, setLoadingPermissions] = useState(false);
  const [, setError] = useState<string | null>(null);

  // Fetch users and permissions when modal opens
  useEffect(() => {
    if (open && role) {
      // Fetch users
      setLoadingUsers(true);
      RolesServiceHelpers.rolesControllerGetUsers(role.id)
        .then((response: any) => {
          setUsers(response.data || []);
        })
        .catch((err: any) => {
          console.error('Error fetching users:', err);
          setUsers([]);
        })
        .finally(() => {
          setLoadingUsers(false);
        });

      // Fetch permissions
      setLoadingPermissions(true);
      RolesServiceHelpers.rolesControllerGetPermissions(role.id)
        .then((response: any) => {
          // Manejar diferentes formatos de respuesta
          let permissionsData: Permission[] = [];

          if (Array.isArray(response)) {
            // Si la respuesta es directamente un array
            permissionsData = response;
          } else if (response?.data && Array.isArray(response.data)) {
            // Si la respuesta está envuelta en un objeto con data
            permissionsData = response.data;
          } else if (response?.permissions && Array.isArray(response.permissions)) {
            // Si la respuesta tiene una propiedad permissions
            permissionsData = response.permissions;
          }

          setPermissions(permissionsData);
        })
        .catch((err: any) => {
          console.error('Error fetching permissions:', err);
          console.error('Error response:', err.response);
          setPermissions([]);
        })
        .finally(() => {
          setLoadingPermissions(false);
        });
    } else {
      // Reset when modal closes
      setUsers([]);
      setPermissions([]);
      setError(null);
    }
  }, [open, role]);

  if (!role) return null;

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateString;
    }
  };

  // Helper functions to get Spanish names from system structure
  const getModuleName = (moduleId: string): string => {
    const module = getModuleById(moduleId);
    return module?.name || moduleId;
  };

  const getMenuName = (moduleId: string, menuId: string): string => {
    const menu = getMenuById(menuId);
    return menu?.name || menuId;
  };

  const getActionName = (moduleId: string, menuId: string, actionId: string): string => {
    const menu = getMenuById(menuId);
    if (!menu) return actionId;

    // Check in mainButtons
    const mainButton = menu.mainButtons?.find((btn) => btn.action === actionId);
    if (mainButton) return mainButton.name;

    // Check in rowActions
    const rowAction = menu.rowActions?.find((action) => action.action === actionId);
    if (rowAction) return rowAction.name;

    const flow = menu.salesFlowActions?.find((f) => f.id === actionId || f.action === actionId);
    if (flow) return flow.name;

    const fieldFlag = menu.fieldFlags?.find((f) => f.id === actionId || f.action === actionId);
    if (fieldFlag) return fieldFlag.name;

    for (const group of menu.fieldFlagGroups ?? []) {
      const inGroup = group.flags.find((f) => f.id === actionId || f.action === actionId);
      if (inGroup) return inGroup.name;
    }

    for (const section of menu.fieldFlagSections ?? []) {
      for (const group of section.groups) {
        const inGroup = group.flags.find((f) => f.id === actionId || f.action === actionId);
        if (inGroup) return inGroup.name;
      }
    }

    const columnFlag = menu.columnFlags?.find((f) => f.id === actionId || f.action === actionId);
    if (columnFlag) return columnFlag.name;

    // Fallback to actionId if not found
    return actionId;
  };

  // Group permissions by module and menu
  const groupedPermissions = permissions.reduce(
    (acc, perm) => {
      const key = `${perm.module}-${perm.menu}`;
      if (!acc[key]) {
        acc[key] = {
          module: perm.module,
          menu: perm.menu,
          actions: [],
        };
      }
      acc[key].actions.push(perm);
      return acc;
    },
    {} as Record<string, { module: string; menu: string; actions: Permission[] }>,
  );

  return (
    <DetailModal
      open={open}
      onClose={onClose}
      title="Detalle del Rol"
      maxWidth="md"
      className={className}
      footerActions={
        <Box sx={{ display: 'flex', gap: 1, width: '100%', justifyContent: 'flex-end' }}>
          {onEditRole && role && !isSystemRole(role) && (
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={() => {
                onEditRole(role.id);
                onClose();
              }}
            >
              Editar
            </Button>
          )}
          <Button variant="outlined" onClick={onClose}>
            Cerrar
          </Button>
        </Box>
      }
    >
      <RoleDetailModalStyles.Content>
        {/* Información Básica */}
        <RoleDetailModalStyles.Section>
          <RoleDetailModalStyles.SectionHeader>
            <RoleIcon fontSize="small" sx={{ mr: 1 }} />
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
              Información Básica
            </Typography>
          </RoleDetailModalStyles.SectionHeader>

          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Box
                sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}
              >
                <RoleDetailModalStyles.InfoRow sx={{ flex: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                    Nombre del Rol
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography
                      variant="body1"
                      sx={{ fontWeight: 600, fontSize: '1.125rem', color: 'text.primary' }}
                    >
                      {role.name}
                    </Typography>
                    {isSystemRole(role) && (
                      <Chip
                        label="Sistema"
                        size="small"
                        sx={(theme) => ({
                          backgroundColor:
                            theme.palette.mode === 'dark'
                              ? alpha(theme.palette.common.white, 0.12)
                              : theme.palette.grey[200],
                          color: theme.palette.text.secondary,
                          fontSize: '0.75rem',
                          height: 20,
                        })}
                      />
                    )}
                  </Box>
                </RoleDetailModalStyles.InfoRow>
                <StatusChip
                  label={role.isActive ? 'Activo' : 'Bloqueado'}
                  status={role.isActive ? 'success' : 'error'}
                  size="small"
                  density="compact"
                  visualStyle="quiet"
                  withDot
                />
              </Box>
            </Grid>

            <Grid item xs={12}>
              <RoleDetailModalStyles.InfoRow>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                  Descripción
                </Typography>
                <Typography variant="body1" color="text.primary">
                  {role.description || '—'}
                </Typography>
              </RoleDetailModalStyles.InfoRow>
            </Grid>
          </Grid>
        </RoleDetailModalStyles.Section>

        <Divider sx={{ my: 3, borderColor: 'divider' }} />

        {/* Permisos */}
        <RoleDetailModalStyles.Section>
          <RoleDetailModalStyles.SectionHeader>
            <BlockIcon fontSize="small" sx={{ mr: 1 }} />
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
              Permisos Asignados ({permissions.length})
            </Typography>
          </RoleDetailModalStyles.SectionHeader>

          {loadingPermissions ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : permissions.length > 0 ? (
            <Box sx={{ mt: 2 }}>
              {Object.values(groupedPermissions).map((group, index) => (
                <Box
                  key={index}
                  sx={(theme) => ({
                    p: 2,
                    mb: 2,
                    borderRadius: 1,
                    border: `1px solid ${theme.palette.divider}`,
                    backgroundColor:
                      theme.palette.mode === 'dark'
                        ? alpha(theme.palette.common.white, 0.06)
                        : theme.palette.grey[50],
                  })}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}
                  >
                    {getModuleName(group.module)} → {getMenuName(group.module, group.menu)}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {group.actions.map((action) => (
                      <Chip
                        key={action.id}
                        label={getActionName(group.module, group.menu, action.action)}
                        size="small"
                        sx={(theme) => ({
                          backgroundColor: alpha(
                            theme.palette.info.main,
                            theme.palette.mode === 'dark' ? 0.28 : 0.18,
                          ),
                          color:
                            theme.palette.mode === 'dark'
                              ? theme.palette.info.light
                              : theme.palette.info.dark,
                          fontSize: '0.75rem',
                          fontWeight: 600,
                        })}
                      />
                    ))}
                  </Box>
                </Box>
              ))}
            </Box>
          ) : (
            <Box
              sx={(theme) => ({
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1.5,
                backgroundColor: alpha(
                  theme.palette.success.main,
                  theme.palette.mode === 'dark' ? 0.18 : 0.08,
                ),
                borderRadius: '8px',
                padding: '10px 14px',
                mt: 2,
                border: `1px solid ${alpha(theme.palette.success.main, 0.35)}`,
              })}
            >
              <InfoIcon
                sx={(theme) => ({
                  fontSize: 18,
                  color: theme.palette.success.light,
                  mt: '2px',
                  flexShrink: 0,
                })}
              />
              <Typography
                variant="body2"
                sx={(theme) => ({
                  color: theme.palette.success.light,
                  lineHeight: 1.5,
                })}
              >
                No hay permisos asignados a este rol.
              </Typography>
            </Box>
          )}
        </RoleDetailModalStyles.Section>

        <Divider sx={{ my: 3, borderColor: 'divider' }} />

        {/* Usuarios Asignados */}
        <RoleDetailModalStyles.Section>
          <RoleDetailModalStyles.SectionHeader>
            <PeopleIcon fontSize="small" sx={{ mr: 1 }} />
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
              Usuarios Asignados ({users.length})
            </Typography>
          </RoleDetailModalStyles.SectionHeader>

          {loadingUsers ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : users.length > 0 ? (
            <Box sx={{ mt: 2 }}>
              {users.map((user) => (
                <Box
                  key={user.id}
                  sx={(theme) => ({
                    p: 2,
                    mb: 1,
                    borderRadius: 1,
                    border: `1px solid ${theme.palette.divider}`,
                    backgroundColor:
                      theme.palette.mode === 'dark'
                        ? alpha(theme.palette.common.white, 0.04)
                        : 'transparent',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  })}
                >
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                      {`${user.firstName} ${user.lastName}`}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {user.email}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          ) : (
            <Alert severity="info" sx={{ mt: 2 }}>
              No hay usuarios asignados a este rol.
            </Alert>
          )}
        </RoleDetailModalStyles.Section>

        <Divider sx={{ my: 3, borderColor: 'divider' }} />

        {/* Estadísticas */}
        <RoleDetailModalStyles.Section>
          <RoleDetailModalStyles.SectionHeader>
            <PeopleIcon fontSize="small" sx={{ mr: 1 }} />
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
              Estadísticas
            </Typography>
          </RoleDetailModalStyles.SectionHeader>

          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={4}>
              <RoleDetailModalStyles.StatCard>
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.light' }}>
                  {role.userCount}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Usuarios asignados
                </Typography>
              </RoleDetailModalStyles.StatCard>
            </Grid>

            <Grid item xs={12} sm={4}>
              <RoleDetailModalStyles.StatCard>
                <CalendarIcon sx={{ fontSize: 32, color: 'primary.light', mb: 1 }} />
                <Typography variant="caption" color="text.secondary">
                  Creado: {formatDate(role.createdAt)}
                </Typography>
              </RoleDetailModalStyles.StatCard>
            </Grid>

            <Grid item xs={12} sm={4}>
              <RoleDetailModalStyles.StatCard>
                <CalendarIcon sx={{ fontSize: 32, color: 'primary.light', mb: 1 }} />
                <Typography variant="caption" color="text.secondary">
                  Modificado: {formatDate(role.updatedAt)}
                </Typography>
              </RoleDetailModalStyles.StatCard>
            </Grid>
          </Grid>
        </RoleDetailModalStyles.Section>
      </RoleDetailModalStyles.Content>
    </DetailModal>
  );
};

export default RoleDetailModalComponent;
